import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaSpinner, FaMapMarker } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';

const StateManagement = () => {
    const [states, setStates] = useState([]);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingState, setEditingState] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        country_id: ''
    });
    const [filterCountry, setFilterCountry] = useState('');

    useEffect(() => {
        fetchCountries();
        fetchStates();
    }, []);

    useEffect(() => {
        fetchStates();
    }, [filterCountry]);

    const fetchCountries = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get('https://app.bihebari.com.np/api/admin/states/countries', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setCountries(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching countries:', error);
            toast.error('Error loading countries');
        }
    };

    const fetchStates = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            const url = filterCountry 
                ? `https://app.bihebari.com.np/api/admin/states?country_id=${filterCountry}`
                : 'https://app.bihebari.com.np/api/admin/states';
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setStates(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching states:', error);
            toast.error('Error loading states');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (state = null) => {
        if (state) {
            setEditingState(state);
            setFormData({
                name: state.name,
                country_id: state.country_id
            });
        } else {
            setEditingState(null);
            setFormData({
                name: '',
                country_id: ''
            });
        }
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setEditingState(null);
        setFormData({
            name: '',
            country_id: ''
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name || !formData.country_id) {
            toast.error('Please fill all required fields');
            return;
        }
        
        setLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            const url = editingState 
                ? `https://app.bihebari.com.np/api/admin/states/${editingState.state_id}`
                : 'https://app.bihebari.com.np/api/admin/states';
            const method = editingState ? 'put' : 'post';
            
            await axios[method](url, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            toast.success(editingState ? 'State updated successfully' : 'State added successfully');
            handleCloseModal();
            fetchStates();
        } catch (error) {
            console.error('Error saving state:', error);
            toast.error(error.response?.data?.message || 'Error saving state');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (stateId, stateName) => {
        if (window.confirm(`Are you sure you want to delete "${stateName}"?`)) {
            try {
                const token = localStorage.getItem('adminToken');
                await axios.delete(`https://app.bihebari.com.np/api/admin/states/${stateId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('State deleted successfully');
                fetchStates();
            } catch (error) {
                console.error('Error deleting state:', error);
                toast.error('Error deleting state');
            }
        }
    };

    const getCountryName = (countryId) => {
        const country = countries.find(c => c.id === countryId);
        return country ? country.name : 'Unknown';
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">State Management</h1>
                <button
                    onClick={() => handleOpenModal()}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition"
                >
                    <FaPlus />
                    <span>Add New State</span>
                </button>
            </div>

            {/* Filter Bar */}
            <div className="bg-white rounded-xl shadow-md p-4 mb-6">
                <div className="flex gap-4 items-end">
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Country</label>
                        <select
                            value={filterCountry}
                            onChange={(e) => setFilterCountry(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        >
                            <option value="">All Countries</option>
                            {countries.map(country => (
                                <option key={country.id} value={country.id}>{country.name}</option>
                            ))}
                        </select>
                    </div>
                    {filterCountry && (
                        <button
                            onClick={() => setFilterCountry('')}
                            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
                        >
                            Clear Filter
                        </button>
                    )}
                </div>
            </div>

            {/* States Table */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">State Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Country</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center">
                                        <FaSpinner className="text-4xl text-indigo-600 animate-spin mx-auto" />
                                    </td>
                                </tr>
                            ) : states.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                                        No states found
                                    </td>
                                </tr>
                            ) : (
                                states.map((state) => (
                                    <tr key={state.state_id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm text-gray-900">{state.state_id}</td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center">
                                                <FaMapMarker className="mr-2 text-indigo-500" />
                                                <span className="text-sm font-medium text-gray-900">{state.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">
                                            {state.country_name || getCountryName(state.country_id)}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">
                                            {new Date(state.created).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => handleOpenModal(state)}
                                                    className="text-blue-600 hover:text-blue-800 transition p-1"
                                                    title="Edit State"
                                                >
                                                    <FaEdit size={18} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(state.state_id, state.name)}
                                                    className="text-red-600 hover:text-red-800 transition p-1"
                                                    title="Delete State"
                                                >
                                                    <FaTrash size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add/Edit Modal */}
            {modalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
                        <div className="flex justify-between items-center p-6 border-b">
                            <h2 className="text-xl font-bold text-gray-800">
                                {editingState ? 'Edit State' : 'Add New State'}
                            </h2>
                            <button onClick={handleCloseModal} className="text-gray-500 hover:text-gray-700">
                                <FaTimes />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Country <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="country_id"
                                        value={formData.country_id}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                        required
                                    >
                                        <option value="">Select Country</option>
                                        {countries.map(country => (
                                            <option key={country.id} value={country.id}>{country.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        State Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="Enter state name"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="px-4 py-2 border rounded-lg hover:bg-gray-50 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center space-x-2"
                                >
                                    {loading ? <FaSpinner className="animate-spin" /> : <FaSave />}
                                    <span>{editingState ? 'Update' : 'Save'}</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StateManagement;